import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { cacheUser, cachedUser } from "@/lib/offline";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = user
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const onExpired = () => setUser(false);
    window.addEventListener("auth:expired", onExpired);
    const tick = setInterval(() => { api.post("/auth/refresh").catch(() => {}); }, 12 * 60 * 1000);
    return () => { window.removeEventListener("auth:expired", onExpired); clearInterval(tick); };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        cacheUser(data);
      } catch (e) {
        const cached = !e?.response ? cachedUser() : null;
        setUser(cached ? { ...cached, offline: true } : false);
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    cacheUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    cacheUser(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, checking, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};
